import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useForm from '@afosto/utils-react/validation/useForm';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useSessionManager from '@afosto/auth-react/useSessionManager';
import { FormattedMessage } from 'react-intl';
import Avatar from '@material-ui/core/Avatar';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Collapse from '@material-ui/core/Collapse';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import InputAdornment from '@material-ui/core/InputAdornment';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import Typography from '@material-ui/core/Typography';
import Dialog from '../Dialog';
import Form from '../Form';
import TextField from '../TextField';
import gravatarUrl from '@afosto/utils/gravatarUrl';
import purify from '@afosto/utils/string/purify';
import sortArrayByProperty from '@afosto/utils/array/sortArrayByProperty';
import { subtenants as subtenantsApi } from '@afosto/iam/api/Subtenants';
import validationSchema from './validations';
import styled from 'styled-components';

const SubtenantList = styled(List)`
  && {
    max-height: 400px;
    min-width: 400px;
    overflow: auto;
  }
`;

const SubtenantDialogContent = styled(DialogContent)`
  && {
    padding: ${props => props.theme.spacing()}px ${props => props.theme.spacing(2)}px;
  }
`;

const filterSubtenantSuggestions = (suggestions, value) => {
  const inputValue = value.trim().toLowerCase();
  return suggestions.filter(
    ({ company, signee }) =>
      company.toLowerCase().indexOf(inputValue) > -1 ||
      signee.toLowerCase().indexOf(inputValue) > -1,
  );
};

const SubtenantDialog = ({ isOpen, onClose }) => {
  const intl = useIntl();
  const [tenant, setTenant] = useState(null);
  const [subtenants, setSubtenants] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [isSearchingSubtenants, setIsSearchingSubtenants] = useState(false);
  const [subtenantResults, setSubtenantResults] = useState([]);
  const [showSubtenantForm, setShowSubtenantForm] = useState(false);
  const [searchTimer, setSearchTimer] = useState(null);
  const sessionManager = useSessionManager();
  const { tenant: tokenTenant } = sessionManager?.tokenMetadata ?? {};

  const fetchTenant = useCallback(async () => {
    try {
      const response = await subtenantsApi.getDefaultSubtenant();
      setTenant(response.data);
    } catch (err) {
      setTenant(null);
    }
  }, []);

  const fetchSubtenants = useCallback(async () => {
    try {
      const response = await subtenantsApi.listSubtenants();
      const results = (response.data || []).filter(result => result.tenant_id !== tokenTenant);
      const orderedSubtenants = sortArrayByProperty(results, 'name');
      setSubtenants(orderedSubtenants);
    } catch (err) {
      setSubtenants([]);
    }
  }, [tokenTenant]);

  useEffect(() => {
    if (isOpen) {
      fetchTenant();
      fetchSubtenants();
    } else {
      setSearchValue('');
      setSubtenantResults([]);
      setShowSubtenantForm(false);
      setIsSearchingSubtenants(false);
      setSearchTimer(null);
    }
  }, [isOpen, fetchTenant, fetchSubtenants]);

  const handleCancel = () => {
    setShowSubtenantForm(false);
  };

  const handleSwitchToSubtenant = subtenant => () => {
    sessionManager.subtenant = subtenant.tenant_id;
    sessionManager.authorize();
  };

  const handleSearchSubtenants = event => {
    const { value } = event.target;

    if (searchTimer) {
      clearTimeout(searchTimer);
    }

    setIsSearchingSubtenants(true);

    if (value && value !== '') {
      const results = filterSubtenantSuggestions([...subtenants], event.target.value);
      setSubtenantResults(results);
    } else {
      setSubtenantResults([]);
    }

    const timer = setTimeout(() => {
      setIsSearchingSubtenants(false);
    }, 400);
    setSearchTimer(timer);
  };

  const requestSubtenantAccess = async ({ signee }) => {
    try {
      await subtenantsApi.sendSubtenantRequest({
        signee,
        roles: ['admin'],
      });

      await fetchSubtenants();
      setShowSubtenantForm(false);
    } catch (err) {
      // Do something with error.
    }
  };

  const form = useForm({
    validationSchema,
    defaultValues: {
      email: '',
    },
    onSubmit: async data => requestSubtenantAccess({ signee: data.email }),
  });

  const regex = new RegExp(searchValue, 'i');

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm">
      {showSubtenantForm ? (
        <>
          <DialogTitle>
            <FormattedMessage
              id="app.dock.userMenu.addNewTenant.title"
              defaultMessage="Add new organization"
              description="The title of the action for requesting access to a new tenant"
            />
          </DialogTitle>
          <Form form={form} renderActions={false}>
            <DialogContent>
              <DialogContentText>
                <FormattedMessage
                  id="app.dock.userMenu.subtenantForm.description"
                  defaultMessage="To request access to another organization, please enter the email address of the organization you would like to request access to"
                  description="Description of the subtenant form"
                />
              </DialogContentText>
              <TextField
                name="email"
                label={intl.formatMessage({
                  id: 'app.dock.userMenu.subtenantForm.fields.signee',
                  defaultMessage: 'E-mail',
                  description: 'The label of the signee field in the subtenant form',
                })}
                autoComplete="off"
                autoFocus
              />
            </DialogContent>
            <DialogActions>
              <Button
                type="button"
                variant="outlined"
                onClick={handleCancel}
                style={{ marginRight: 8 }}
              >
                <FormattedMessage
                  id="app.dock.userMenu.subtenantForm.actions.cancel"
                  defaultMessage="Cancel"
                  description="The label of the cancel button of the subtenant form"
                />
              </Button>
              <Button type="submit" color="primary" variant="contained">
                <FormattedMessage
                  id="app.dock.userMenu.subtenantForm.actions.sent"
                  defaultMessage="Sent"
                  description="The label of the submit button of the subtenant form"
                />
              </Button>
            </DialogActions>
          </Form>
        </>
      ) : (
        <>
          <DialogTitle>
            <FormattedMessage
              id="app.dock.userMenu.selectTenant"
              defaultMessage="Select an organization"
              description="Title of the switch tenant dialog in the user menu"
            />
          </DialogTitle>
          <SubtenantDialogContent>
            <TextField
              id="select-subtenant"
              name="subtenant"
              margin="none"
              onKeyUp={handleSearchSubtenants}
              onChange={event => setSearchValue(event.target.value)}
              value={searchValue}
              autoComplete="off"
              placeholder={intl.formatMessage({
                id: 'app.dock.userMenu.searchTenants.placeholder',
                defaultMessage: 'Search organizations',
                description: 'The placeholder of the search tenants field',
              })}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {isSearchingSubtenants && <CircularProgress size={20} />}
                  </InputAdornment>
                ),
              }}
            />
          </SubtenantDialogContent>
          <Collapse in={tenant && tenant.tenant_id !== tokenTenant && searchValue.length <= 0}>
            <List disablePadding>
              <ListItem onClick={handleSwitchToSubtenant(tenant)} button>
                <ListItemAvatar>
                  <Avatar
                    alt={tenant ? tenant.company : ''}
                    src={gravatarUrl(tenant ? tenant.signee : '', {
                      letterValue: tenant ? tenant.company : 'unknown',
                    })}
                  />
                </ListItemAvatar>
                <ListItemText
                  primary={`${tenant ? tenant.company : ''} (${intl.formatMessage({
                    id: 'app.default',
                    defaultMessage: 'Default',
                    description: 'The label for default',
                  })})`}
                  secondary={tenant ? tenant.signee : ''}
                />
              </ListItem>
            </List>
          </Collapse>
          <Collapse in={subtenantResults.length > 0 && !isSearchingSubtenants}>
            <SubtenantList disablePadding>
              {subtenantResults.map((subtenant, idx) => (
                <ListItem
                  key={subtenant.tenant_id}
                  divider={idx !== subtenantResults.length - 1}
                  onClick={handleSwitchToSubtenant(subtenant)}
                  button
                >
                  <ListItemAvatar>
                    <Avatar
                      alt={subtenant.company}
                      src={gravatarUrl(subtenant.signee, { letterValue: subtenant.company })}
                    />
                  </ListItemAvatar>
                  {/* eslint-disable-next-line react/no-danger */}
                  <ListItemText>
                    <Typography
                      variant="subtitle1"
                      dangerouslySetInnerHTML={{
                        __html: purify(subtenant.company.replace(regex, '<strong>$&</strong>')),
                      }}
                    />
                    <Typography
                      variant="body2"
                      dangerouslySetInnerHTML={{
                        __html: purify(subtenant.signee.replace(regex, '<strong>$&</strong>')),
                      }}
                    />
                  </ListItemText>
                </ListItem>
              ))}
            </SubtenantList>
          </Collapse>
        </>
      )}
    </Dialog>
  );
};

SubtenantDialog.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
};

SubtenantDialog.defaultProps = {
  isOpen: false,
  onClose: undefined,
};

export default SubtenantDialog;
